import React from 'react';
import RVDivider from '../RVDivider';
import { evolve, pipe, cond, T } from 'ramda';

import {
  classnames,
  makeNegative,
  makePositive,
  currencyFormatter as formatter
} from '../../../utilities';

import './RVTooltipContent.scss';

export const MoneyRow = ({ text = '', value = 0, isBold = false }) => (
  <div
    className={classnames('rvtooltipcontent__moneyrow', {
      'bold-text': isBold
    })}
  >
    <span>{text}</span>
    <span>{formatter.format(value)}</span>
  </div>
);

export const BenefitSection = ({ benefitText }) => (
  <>
    <div className="bold-text margin-top uppercase-text">Carvana Benefit:</div>
    <p>{benefitText}</p>
  </>
);

const RVTooltipContent = ({ children }) => (
  <div className="rvtooltipcontent">{children}</div>
);

const RVPositiveEquityTooltipContentDisplay = ({
  tradeinOffer = 0,
  currentLoanBalance = 0,
  benefitText = 'Your trade-in will reduce or eliminate your required down payment, and could even reduce your monthly payments, too.'
}) => {
  return (
    <RVTooltipContent>
      <MoneyRow text="Trade-in Offer" value={tradeinOffer} />
      <MoneyRow text="Current Loan Balance" value={currentLoanBalance} />
      <RVDivider />
      <MoneyRow
        isBold
        text="Trade-in Credit"
        value={tradeinOffer + currentLoanBalance}
      />
      <BenefitSection benefitText={benefitText} />
    </RVTooltipContent>
  );
};

export const RVPositiveEquityTooltipContent = pipe(
  evolve({
    currentLoanBalance: makeNegative,
    tradeinOffer: makePositive
  }),
  RVPositiveEquityTooltipContentDisplay
);

const RVNegativeEquityTooltipContentDisplay = ({
  tradeinOffer = 0,
  currentLoanBalance = 0,
  benefitText = 'To lower the amount you owe after your trade-in, we let you transfer the balance you owe to your new Carvana loan.'
}) => {
  return (
    <RVTooltipContent>
      <MoneyRow text="Current Loan Balance" value={currentLoanBalance} />
      <MoneyRow text="Trade-in Offer" value={tradeinOffer} />
      <RVDivider />
      <MoneyRow
        isBold
        text="Transferred to New Loan"
        value={tradeinOffer + currentLoanBalance}
      />
      <BenefitSection benefitText={benefitText} />
    </RVTooltipContent>
  );
};

export const RVNegativeEquityTooltipContent = pipe(
  evolve({
    currentLoanBalance: makePositive,
    tradeinOffer: makeNegative
  }),
  RVNegativeEquityTooltipContentDisplay
);

const RVLargeNegativeEquityTooltipContentDisplay = ({
  tradeinOffer = 0,
  currentLoanBalance = 0,
  benefitText = 'To lower the amount you owe after your trade-in, we let you transfer up to $2,500 of your balance to your new Carvana loan. The remainder of your balance will be added to your down payment.'
}) => {
  return (
    <RVTooltipContent>
      <MoneyRow text="Current Loan Balance" value={currentLoanBalance} />
      <MoneyRow text="Trade-in Offer" value={tradeinOffer} />
      <RVDivider />
      <MoneyRow
        isBold
        text="Trade-in Balance"
        value={tradeinOffer + currentLoanBalance}
      />
      <RVDivider />
      <MoneyRow
        isBold
        text="Added to Down Payment"
        value={tradeinOffer + currentLoanBalance}
      />
      <BenefitSection benefitText={benefitText} />
    </RVTooltipContent>
  );
};

export const RVLargeNegativeEquityTooltipContent = pipe(
  evolve({
    currentLoanBalance: makePositive,
    tradeinOffer: makeNegative
  }),
  RVLargeNegativeEquityTooltipContentDisplay
);

const isNegativeEquity = ({ currentLoanBalance, tradeinOffer }) =>
  currentLoanBalance > tradeinOffer;

export const RVEquityTooltipContent = pipe(
  evolve({
    currentLoanBalance: makePositive,
    tradeinOffer: makePositive
  }),
  cond([
    [isNegativeEquity, RVNegativeEquityTooltipContent],
    [T, RVPositiveEquityTooltipContent]
  ])
);

export default RVTooltipContent;
